import { BufferGeometry, Vector3, Object3D, Box3 } from "three";
import { create, all } from 'mathjs';
import {
  LOAD_INDICATOR_BBOX_EPSILON,
  LOAD_INDICATOR_DEFAULT_LENGTH,
  LOAD_INDICATOR_SCALE_FACTOR
} from './engine.constants';
const math = create(all);

// Arrow length for load/moment indicators, scaled to the assembly bbox.
// Falls back to LOAD_INDICATOR_DEFAULT_LENGTH for empty/degenerate scenes.
export const getLoadIndicatorScale = (size: Vector3): number => {
  const maxDim = Math.max(size.x, size.y, size.z);
  return maxDim > LOAD_INDICATOR_BBOX_EPSILON
    ? maxDim * LOAD_INDICATOR_SCALE_FACTOR
    : LOAD_INDICATOR_DEFAULT_LENGTH;
};

// Calculate volume
export const calculateVolume = (faces: { vertices: number[], indices: number[] }[]): number => {
    let volume = 0;

    // Reuse vectors to minimize object creation
    const a = [0, 0, 0];
    const b = [0, 0, 0];
    const c = [0, 0, 0];

    for (const face of faces) {
        const { vertices, indices } = face;
        for (let i = 0; i < indices.length; i += 3) {
            const ai = indices[i] * 3;
            const bi = indices[i + 1] * 3;
            const ci = indices[i + 2] * 3;

            [a[0], a[1], a[2]] = [vertices[ai], vertices[ai + 1], vertices[ai + 2]];
            [b[0], b[1], b[2]] = [vertices[bi], vertices[bi + 1], vertices[bi + 2]];
            [c[0], c[1], c[2]] = [vertices[ci], vertices[ci + 1], vertices[ci + 2]];

            volume += signedVolumeOfTriangle(a, b, c);
        }
    }

    return Math.abs(volume);
}

const signedVolumeOfTriangle = (a: number[], b: number[], c: number[]): number => {
    return (a[0] * (b[1] * c[2] - b[2] * c[1]) +
        a[1] * (b[2] * c[0] - b[0] * c[2]) +
        a[2] * (b[0] * c[1] - b[1] * c[0])) / 6.0;
}

// Calculate Area
export type Point3D = [number, number, number] | number[];
export interface FaceData { vertices: number[]; indices: number[]; }
export interface GeometryProperties { volume: number; area: number; }
export interface Part extends GeometryProperties { id: string | number; part: any }
const v_subtract = (v1: Point3D, v2: Point3D): Point3D => [v1[0] - v2[0], v1[1] - v2[1], v1[2] - v2[2]];
const v_crossProduct = (v1: Point3D, v2: Point3D): Point3D => [v1[1] * v2[2] - v1[2] * v2[1], v1[2] * v2[0] - v1[0] * v2[2], v1[0] * v2[1] - v1[1] * v2[0]];
const v_magnitude = (v: Point3D): number => Math.sqrt(v[0] ** 2 + v[1] ** 2 + v[2] ** 2);
const calculateTriangleArea = (p1: Point3D, p2: Point3D, p3: Point3D): number => {
    const edge1 = v_subtract(p2, p1);
    const edge2 = v_subtract(p3, p1);
    const crossProd = v_crossProduct(edge1, edge2);
    return 0.5 * v_magnitude(crossProd);
};

export const calculateArea = (faces: FaceData[]): number => {
    let totalArea = 0;

    // Reuse point coordinate arrays to minimize object creation per loop
    const p1_coords: Point3D = [0, 0, 0];
    const p2_coords: Point3D = [0, 0, 0];
    const p3_coords: Point3D = [0, 0, 0];

    for (const face of faces) {
        const { vertices, indices } = face;
        const numTrianglesInFace = Math.floor(indices.length / 3);

        for (let i = 0; i < numTrianglesInFace; ++i) {
            // Get the indices of the vertices that form the i-th triangle
            const v_idx1 = indices[i * 3];
            const v_idx2 = indices[i * 3 + 1];
            const v_idx3 = indices[i * 3 + 2];

            // Convert vertex indices to starting indices in the flattened coordinate array
            const coord_start_idx1 = v_idx1 * 3;
            const coord_start_idx2 = v_idx2 * 3;
            const coord_start_idx3 = v_idx3 * 3;

            // Bounds check: ensure all coordinates for these vertices are within the 'vertices' array
            if (coord_start_idx1 + 2 >= vertices.length ||
                coord_start_idx2 + 2 >= vertices.length ||
                coord_start_idx3 + 2 >= vertices.length) {
                console.warn(`calculateArea: Vertex index out of bounds for face's vertices array. Skipping triangle. Vertex indices: ${v_idx1},${v_idx2},${v_idx3}. Vertices length: ${vertices.length / 3}.`);
                continue;
            }

            p1_coords[0] = vertices[coord_start_idx1];
            p1_coords[1] = vertices[coord_start_idx1 + 1];
            p1_coords[2] = vertices[coord_start_idx1 + 2];

            p2_coords[0] = vertices[coord_start_idx2];
            p2_coords[1] = vertices[coord_start_idx2 + 1];
            p2_coords[2] = vertices[coord_start_idx2 + 2];

            p3_coords[0] = vertices[coord_start_idx3];
            p3_coords[1] = vertices[coord_start_idx3 + 1];
            p3_coords[2] = vertices[coord_start_idx3 + 2];

            totalArea += calculateTriangleArea(p1_coords, p2_coords, p3_coords);
        }
    }
    return totalArea;
};

/**
 * Pick the densest *coplanar* group of non-design parts to use as boundary
 * conditions — the heuristic from the load-case editor "Constraints" tab.
 * Buckets parts by the floored |x|/|y|/|z| of their world center, then returns
 * the single axis-position bucket holding the most parts (e.g. the row of bolt
 * holes on the mounting face). Drop-in for findSimilarPartsFlat (Part[] →
 * Part[]); used everywhere so the panel "Auto suggest", the AI auto-config, and
 * the editor tab all select BCs the same way. Similarity-based selection
 * returned nothing for non-identical parts; this always finds the mounting plane.
 */
export function selectDensestCoplanarParts(parts: Part[]): Part[] {
    if (parts.length === 0) return [];
    const buckets: { [axis: string]: { [pos: string]: Part[] } } = { x: {}, y: {}, z: {} };
    for (const p of parts) {
        const group = (p.part as any)?.group as Object3D | undefined;
        if (!group) continue;
        const center = new Box3().setFromObject(group).getCenter(new Vector3());
        center.applyMatrix4(group.matrixWorld.clone().invert());
        group.localToWorld(center);
        const keys: [string, string][] = [
            ['x', Math.abs(Math.floor(center.x)).toString()],
            ['y', Math.abs(Math.floor(center.y)).toString()],
            ['z', Math.abs(Math.floor(center.z)).toString()],
        ];
        for (const [axis, pos] of keys) {
            (buckets[axis][pos] ??= []).push(p);
        }
    }
    let max = 0;
    let best: Part[] = [];
    for (const axis of ['x', 'y', 'z']) {
        for (const pos of Object.keys(buckets[axis])) {
            if (buckets[axis][pos].length > max) {
                max = buckets[axis][pos].length;
                best = buckets[axis][pos];
            }
        }
    }
    return best;
}

export function findSimilarPartsFlat(
    parts: Part[],
    areaTolerance: number, // Expected as a decimal, e.g., 0.05 for 5%
    volumeTolerance: number // Expected as a decimal, e.g., 0.05 for 5%
): Part[] {
    const n = parts.length;
    if (n === 0) {
        return [];
    }

    // Ensure tolerances are non-negative percentages
    const nonNegativeAreaTolerancePercent = Math.max(0, areaTolerance);
    const nonNegativeVolumeTolerancePercent = Math.max(0, volumeTolerance);

    /**
     * Helper function to determine if two parts are similar based on percentage tolerances.
     */
    const areSimilar = (part1: Part, part2: Part): boolean => {
        const areaDiff = Math.abs(part1.area - part2.area);
        const referenceArea = Math.min(part1.area, part2.area);
        let areaIsSimilar: boolean;

        if (referenceArea === 0) {
            areaIsSimilar = areaDiff === 0;
        } else {
            areaIsSimilar = (areaDiff <= referenceArea * nonNegativeAreaTolerancePercent);
        }

        const volumeDiff = Math.abs(part1.volume - part2.volume);
        const referenceVolume = Math.min(part1.volume, part2.volume);
        let volumeIsSimilar: boolean;

        if (referenceVolume === 0) {
            volumeIsSimilar = volumeDiff === 0;
        } else {
            volumeIsSimilar = (volumeDiff <= referenceVolume * nonNegativeVolumeTolerancePercent);
        }

        return areaIsSimilar && volumeIsSimilar;
    };

    // 1. Build adjacency list for the similarity graph
    const adj: number[][] = Array.from({ length: n }, () => []);
    for (let i = 0; i < n; i++) {
        for (let j = i + 1; j < n; j++) {
            if (areSimilar(parts[i], parts[j])) {
                adj[i].push(j);
                adj[j].push(i);
            }
        }
    }

    // 2. Find connected components (groups) and store them
    const visited: boolean[] = new Array(n).fill(false);
    const allFoundGroups: Part[][] = []; // Stores all groups of similar parts (size > 1)

    for (let i = 0; i < n; i++) {
        if (!visited[i]) {
            const currentGroupIndices: number[] = [];
            const queue: number[] = [i];
            visited[i] = true;

            let head = 0;
            while (head < queue.length) {
                const u = queue[head++];
                currentGroupIndices.push(u);

                for (const v of adj[u]) {
                    if (!visited[v]) {
                        visited[v] = true;
                        queue.push(v);
                    }
                }
            }

            // Only consider groups with more than one member
            if (currentGroupIndices.length > 1) {
                const currentGroupParts = currentGroupIndices.map(index => parts[index]);
                allFoundGroups.push(currentGroupParts);
            }
        }
    }

    if (allFoundGroups.length === 0) {
        return []; // No similarity groups of size > 1 found
    }

    // 3. For each found group, determine its minimum volume
    const groupMinVolumes: { group: Part[], minVol: number }[] = [];
    for (const group of allFoundGroups) {
        if (group.length === 0) continue; // Should not happen due to previous check
        let minVolInThisGroup = Infinity;
        for (const part of group) {
            if (part.volume < minVolInThisGroup) {
                minVolInThisGroup = part.volume;
            }
        }
        if (minVolInThisGroup !== Infinity) { // Ensure the group had parts with valid volumes
            groupMinVolumes.push({ group, minVol: minVolInThisGroup });
        }
    }

    if (groupMinVolumes.length === 0) {
        return []; // No groups with valid minimum volumes
    }

    // 4. Find the overall smallest minimum volume across all groups
    let overallSmallestMinVolume = Infinity;
    for (const entry of groupMinVolumes) {
        if (entry.minVol < overallSmallestMinVolume) {
            overallSmallestMinVolume = entry.minVol;
        }
    }

    // 5. Identify the "target groups" - those whose minimum volume matches the overall smallest
    const targetGroups: Part[][] = [];
    for (const entry of groupMinVolumes) {
        if (entry.minVol === overallSmallestMinVolume) {
            targetGroups.push(entry.group);
        }
    }

    // 6. From these target groups, select the parts
    const finalSelectedParts: Part[] = [];
    for (const group of targetGroups) {
        // Filter for parts in this group that have the overallSmallestMinVolume
        let candidates = group.filter(part => part.volume === overallSmallestMinVolume);

        if (candidates.length > 0) {
            // If there's a tie in volume (more than one candidate), filter by smallest area
            if (candidates.length > 1) {
                let minAreaInCandidates = Infinity;
                for (const part of candidates) {
                    if (part.area < minAreaInCandidates) {
                        minAreaInCandidates = part.area;
                    }
                }
                candidates = candidates.filter(part => part.area === minAreaInCandidates);
            }

            // Add all selected candidates from this target group
            candidates.forEach(part => {
                finalSelectedParts.push(part);
            });
        }
    }

    // Remove duplicates if the same part object was somehow selected multiple times
    // (e.g., if a part could belong to multiple "target groups" - though BFS should prevent this for initial groups)
    // A simple way for now, if IDs are unique and represent distinct parts:
    const uniqueResults = Array.from(new Map(finalSelectedParts.map(part => [part.id, part])).values());

    try {
        if (parts.length === uniqueResults.length) {
            uniqueResults.pop();
        }
    } catch (e) { }

    return uniqueResults;
}

// Others

export const calculateCenter = (object3d: Object3D): Vector3 => {
    object3d.updateWorldMatrix(true, true);
    object3d.updateMatrixWorld(true);
    object3d.updateMatrix();
    const boundingBox = new Box3().setFromObject(object3d);
    return boundingBox.getCenter(new Vector3());
}

export const calculateCenterOfMass = (parts: { center: Vector3, volume: number }[]): Vector3 => {
    let numerator = math.matrix([math.bignumber(0), math.bignumber(0), math.bignumber(0)]);  // Create a 3D zero vector
    let denominator = math.bignumber(0);

    parts.forEach(part => {
        const center = math.matrix([math.bignumber(part.center.x), math.bignumber(part.center.y), math.bignumber(part.center.z)]);
        const scaledCenter = math.multiply(center, math.bignumber(part.volume));
        numerator = math.add(numerator, scaledCenter);
        denominator = math.add(denominator, math.bignumber(part.volume));
    });

    const centerOfMass = math.divide(numerator, denominator);
    const result: any = centerOfMass.valueOf();
    return new Vector3(result[0], result[1], result[2]);
}

export const calculateBoundingBox = (meshes: Object3D[]): Box3 => {
    return meshes.reduce((boundingBox, object) => {
        return boundingBox.union(new Box3().setFromObject(object));
    }, new Box3());
}

export const calculateVolumeFromGeometry = (geometry: BufferGeometry): number => {
    if (!geometry.isBufferGeometry) {
        return 0;
    }
    const isIndexed = geometry.index !== null;
    const position = geometry.getAttribute('position');
    let sum = 0;
    const p1 = new Vector3(),
        p2 = new Vector3(),
        p3 = new Vector3();
    if (!isIndexed) {
        let faces = position.count / 3;
        for (let i = 0; i < faces; i++) {
            p1.fromBufferAttribute(position, i * 3 + 0);
            p2.fromBufferAttribute(position, i * 3 + 1);
            p3.fromBufferAttribute(position, i * 3 + 2);
            sum += signedVolumeOfTriangleThreeJs(p1, p2, p3);
        }
    }
    else {
        const index = geometry.index!;
        const faces = index.count / 3;
        for (let i = 0; i < faces; i++) {
            p1.fromBufferAttribute(position, index.array[i * 3 + 0]);
            p2.fromBufferAttribute(position, index.array[i * 3 + 1]);
            p3.fromBufferAttribute(position, index.array[i * 3 + 2]);
            sum += signedVolumeOfTriangleThreeJs(p1, p2, p3);
        }
    }

    return Math.abs(sum);
}

export const signedVolumeOfTriangleThreeJs = (p1: Vector3, p2: Vector3, p3: Vector3) => {
    return p1.dot(p2.cross(p3)) / 6.0;
}